import React from 'react'
import { Button, Input } from 'antd'
import { CheckOutlined, CloseOutlined, EditOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons'
import { CHAT_MEDIA_ACCEPT, IChatAttachment } from './useChatAttachments'
import styles from './ChatsPage.module.scss'
import type { TextAreaRef } from 'antd/es/input/TextArea'

interface IChatComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  maxLength: number
  disabled?: boolean
  isSubmitting?: boolean
  // Текст сообщения, которое правят: Enter сохраняет правку, а не отправляет новое
  editingText?: string | null
  onCancelEdit?: () => void
  // Сообщение можно отправить без текста: приложены фото или видео или правят подпись к ним
  allowEmptyText?: boolean
  attachments?: IChatAttachment[]
  onAttach?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  onPaste?: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void
}

// Ctrl+Enter браузер в поле ввода не переносит. execCommand оставляет вставку
// в истории Ctrl+Z, запасной путь — для браузера, где он не сработал
const insertLineBreak = (textarea: HTMLTextAreaElement) => {
  if (document.execCommand('insertText', false, '\n')) return

  textarea.setRangeText('\n', textarea.selectionStart, textarea.selectionEnd, 'end')
  textarea.dispatchEvent(new Event('input', { bubbles: true }))
}

export const ChatComposer: React.FC<IChatComposerProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  maxLength,
  disabled = false,
  isSubmitting = false,
  editingText = null,
  onCancelEdit,
  allowEmptyText = false,
  attachments = [],
  onAttach,
  onRemoveAttachment,
  onPaste
}) => {
  const isEditing = editingText !== null
  const textAreaRef = React.useRef<TextAreaRef>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Правку начинают кнопкой в пузыре — курсор переводим в поле, иначе не напечатать и не нажать Esc
  React.useEffect(() => {
    if (editingText !== null) textAreaRef.current?.focus({ cursor: 'end' })
  }, [editingText])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape' && isEditing) {
      event.preventDefault()
      onCancelEdit?.()
      return
    }
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.altKey || event.shiftKey) {
      return
    }
    // На телефоне Enter переносит строку, отправка — кнопкой
    if (window.matchMedia('(hover: none)').matches) return

    event.preventDefault()
    if (event.ctrlKey || event.metaKey) {
      insertLineBreak(event.currentTarget)
      return
    }
    onSubmit()
  }

  return (
    <div className={styles.footer}>
      {isEditing && (
        <div className={styles.editBar}>
          <EditOutlined className={styles.editBarIcon} />
          <div className={styles.editBarText}>
            <span className={styles.editBarTitle}>Редактирование</span>
            <span className={styles.editBarPreview}>{editingText}</span>
          </div>
          <Button
            type='text'
            size='small'
            icon={<CloseOutlined />}
            onClick={onCancelEdit}
            aria-label='Отменить редактирование'
          />
        </div>
      )}
      {!isEditing && attachments.length > 0 && (
        <div className={styles.attachments}>
          {attachments.map((item) => (
            <div key={item.id} className={styles.attachment}>
              {item.kind === 'video' ? (
                <>
                  {/* #t=0.1 — чтобы Safari показал кадр, а не пустой прямоугольник */}
                  <video src={`${item.url}#t=0.1`} className={styles.attachmentImage} muted playsInline preload='metadata' />
                  <span className={styles.playBadge} aria-hidden />
                </>
              ) : (
                <img src={item.url} alt={item.file.name} className={styles.attachmentImage} />
              )}
              <button
                type='button'
                className={styles.attachmentRemove}
                onClick={() => onRemoveAttachment?.(item.id)}
                disabled={isSubmitting}
                aria-label={`Убрать ${item.kind === 'video' ? 'видео' : 'фото'} ${item.file.name}`}
              >
                <CloseOutlined />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className={styles.composerRow}>
        {onAttach && (
          <>
            <Button
              type='text'
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isEditing}
              className={styles.attachButton}
              aria-label='Приложить фото или видео'
              title='Приложить фото или видео'
            />
            <input
              ref={fileInputRef}
              type='file'
              accept={CHAT_MEDIA_ACCEPT}
              multiple
              hidden
              onChange={(event) => {
                onAttach(Array.from(event.target.files || []))
                // Тот же файл можно выбрать снова, если его убрали из превью
                event.target.value = ''
              }}
            />
          </>
        )}
        <div className={styles.composerInput}>
          <Input.TextArea
            ref={textAreaRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            showCount
            onKeyDownCapture={handleKeyDown}
            onPaste={isEditing ? undefined : onPaste}
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
          <span className={styles.composerHint}>
            {isEditing
              ? 'Enter — сохранить, Esc — отменить, Shift+Enter или Ctrl+Enter — перенос строки'
              : 'Enter — отправить, Shift+Enter или Ctrl+Enter — перенос строки'}
          </span>
        </div>
        <Button
          type='primary'
          icon={isEditing ? <CheckOutlined /> : <SendOutlined />}
          disabled={(!value.trim() && !allowEmptyText) || disabled}
          loading={isSubmitting}
          onClick={onSubmit}
          className={styles.sendButton}
          aria-label={isEditing ? 'Сохранить' : 'Отправить'}
        />
      </div>
    </div>
  )
}
